/**
 * Shared category-tree data layer.
 *
 * Single source of truth for the theme's "Category system" (settings group
 * `category_*`). Consumed by:
 *   - assets/shop-by-category.js (JSON source: fetch + render the grid)
 *   - snippets/breadcrumbs.liquid (JSON source: hydrate the ancestor trail)
 *
 * Only the JSON source needs JS — the metafield source is rendered server-side
 * in Liquid (it has the tree at render time). This module is therefore loaded
 * on demand by those consumers, never globally.
 *
 * Exposes (merged onto the `window.theme.categories` object that
 * snippets/theme-globals.liquid already seeds with `{ source, dataUrl }`):
 *   - load(url?, signal?)        → Promise<tree>  fetch + sessionStorage cache
 *   - readCache(url) / writeCache(url, data)      synchronous cache access
 *   - findAncestry(tree, handle) → [{name, handle}, …] root→leaf (inclusive)
 *   - findNode / findParent      → a node, or the node whose children are siblings
 *   - findChildrenOrSiblings(tree, handle, opts) → the set a rail should show
 *   - pruneEmpty(tree)           → copy with product-less subtrees dropped
 *   - normalize / stripDiacritics / buildNormMap   diacritic-insensitive search
 *
 * Tree shape (same JSON schema shop-by-category has always consumed, plus one
 * OPTIONAL field — `products_count` — that whoever generates the file may emit
 * to let the theme's "show empty categories" setting filter this source):
 *   { "Name": { handle: "h", products_count: 12, subcategories: { … } }, … }
 */

(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const cats = (theme.categories = theme.categories || {});

  // Idempotent: the module may be requested by more than one consumer on the
  // same page (e.g. shop-by-category + category breadcrumbs).
  if (cats.load) return;

  // Accent-insensitive match: NFD splits accented chars into base + combining
  // mark, then strip combining marks (Unicode block U+0300..U+036F).
  const stripDiacritics = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '');
  const normalize = (s) => stripDiacritics(String(s).toLowerCase());

  // Per-character normalization map. `normalized[i]` was produced by
  // `original[map[i]]`. Needed because NFD + diacritic-strip changes string
  // length non-uniformly (e.g. "Niño" → "nino"), so a match position in the
  // normalized string can't be reused on the original. The sentinel at
  // `map[normalized.length]` lets us read exclusive end indices safely.
  const buildNormMap = (original) => {
    let normalized = '';
    const map = [];
    for (let i = 0; i < original.length; i++) {
      const charNorm = normalize(original[i]);
      for (let j = 0; j < charNorm.length; j++) map.push(i);
      normalized += charNorm;
    }
    map.push(original.length);
    return { normalized, map };
  };

  // Cache key kept identical to the historical shop-by-category key so existing
  // session caches stay valid across this refactor.
  const cacheKey = (url) => 'shop-by-category:' + url;

  const readCache = (url) => {
    try {
      const raw = sessionStorage.getItem(cacheKey(url));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const writeCache = (url, data) => {
    try {
      sessionStorage.setItem(cacheKey(url), JSON.stringify(data));
    } catch {
      // sessionStorage can throw on quota / private mode. Non-fatal.
    }
  };

  /**
   * Resolve the category tree for `url` (defaults to the theme-configured
   * `dataUrl`). Returns the sessionStorage copy when present, else fetches and
   * caches. Pass an AbortSignal to cancel an inflight request.
   */
  const load = async (url, signal) => {
    url = url || cats.dataUrl;
    if (!url) throw new Error('[category-data] no data URL configured');

    const cached = readCache(url);
    if (cached) return cached;

    const res = await fetch(url, signal ? { signal } : undefined);
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();
    writeCache(url, data);
    return data;
  };

  // A category name in a merchant-supplied JSON file is an arbitrary string,
  // and `__proto__` is the one that must never reach a bracket assignment: on a
  // plain object it is an accessor for the prototype chain, so `out[name] = node`
  // would re-parent the result object instead of adding an entry to it. Nodes
  // keyed that way are dropped rather than rendered.
  //
  // Only the functions that BUILD a map by key need this. The walkers below only
  // read, so a `__proto__` node is harmless to them and they are left alone.
  const isUnsafeKey = (name) => name === '__proto__';

  /**
   * Walk the nested tree for `handle` and return the ancestor path root→leaf,
   * inclusive of the matched node: [{name, handle}, …]. Empty array if the
   * handle is not present in the tree.
   */
  const findAncestry = (tree, handle) => {
    if (!tree || typeof tree !== 'object' || !handle) return [];
    const path = [];

    const walk = (node) => {
      for (const name in node) {
        if (!Object.prototype.hasOwnProperty.call(node, name)) continue;
        const data = node[name];
        if (!data || typeof data !== 'object') continue;

        path.push({ name, handle: data.handle || '' });
        if (data.handle === handle) return true;
        if (data.subcategories && walk(data.subcategories)) return true;
        path.pop();
      }
      return false;
    };

    return walk(tree) ? path : [];
  };

  /**
   * Return the tree node for `handle` (the value object `{ handle,
   * subcategories }`), or null. Used to read a collection's direct children.
   */
  const findNode = (tree, handle) => {
    if (!tree || typeof tree !== 'object' || !handle) return null;
    for (const name in tree) {
      if (!Object.prototype.hasOwnProperty.call(tree, name)) continue;
      const node = tree[name];
      if (!node || typeof node !== 'object') continue;
      if (node.handle === handle) return node;
      if (node.subcategories) {
        const found = findNode(node.subcategories, handle);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * Return the PARENT node of `handle` (so its `.subcategories` are the
   * siblings), or null when `handle` is top-level or absent. Mirrors the
   * `findNode` contract; callers filter out the current handle themselves.
   */
  const findParent = (tree, handle, parent) => {
    if (!tree || typeof tree !== 'object' || !handle) return null;
    for (const name in tree) {
      if (!Object.prototype.hasOwnProperty.call(tree, name)) continue;
      const node = tree[name];
      if (!node || typeof node !== 'object') continue;
      if (node.handle === handle) return parent || null;
      if (node.subcategories) {
        const found = findParent(node.subcategories, handle, node);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * Drop every node whose whole subtree is known to hold no products, for the
   * theme-wide "show empty categories" setting (ADR-0015).
   *
   * The input is NEVER mutated: it may be the sessionStorage-backed tree that
   * breadcrumbs and the rail are reading at the same time. New objects are
   * created only along the paths that changed — an untouched node is returned
   * by reference, so pruning a large tree costs a handful of allocations rather
   * than a full clone. Consumers therefore treat the result as READ-ONLY;
   * nothing in the theme writes to it.
   *
   * A node is KEPT when any of these holds:
   *   - its `products_count` is absent or not a finite number (UNKNOWN never
   *     hides a category — the JSON source is only filterable when whoever
   *     generates the file opts in by emitting counts);
   *   - `products_count > 0`;
   *   - at least one descendant survived the same test (so a purely structural
   *     parent whose products live in its children stays navigable).
   */
  const pruneEmpty = (tree) => {
    if (!tree || typeof tree !== 'object') return tree;

    const out = {};
    for (const name in tree) {
      if (!Object.prototype.hasOwnProperty.call(tree, name) || isUnsafeKey(name)) continue;
      const node = tree[name];
      if (!node || typeof node !== 'object') {
        out[name] = node;
        continue;
      }

      const hadSubs = node.subcategories && typeof node.subcategories === 'object';
      const subs = hadSubs ? pruneEmpty(node.subcategories) : null;
      const keptSubs = subs && Object.keys(subs).length ? subs : null;

      const count = node.products_count;
      const known = typeof count === 'number' && Number.isFinite(count);
      if (known && count <= 0 && !keptSubs) continue;

      out[name] = hadSubs ? Object.assign({}, node, { subcategories: keptSubs || {} }) : node;
    }
    return out;
  };

  /**
   * Resolve the set of nodes a category navigator should show for `handle`:
   * its children, or — when `options.fallback` is 'siblings' — the categories it
   * sits among. Returns a `{ name: node }` map, or null when neither yields
   * anything. Lives here rather than in the rail because it is pure tree work,
   * composed from `findNode`/`findParent` (which ADR-0008 already put here for
   * exactly this lookup) — the custom element is left with only the DOM.
   *
   * `options.dropEmpty` runs `pruneEmpty` on each candidate set BEFORE choosing
   * between them, and that ordering is the whole point: a category whose every
   * child is a dead end has, for the visitor, no subcategories, so it must fall
   * through to its siblings rather than render nothing. The metafield path in
   * sections/category-rail.liquid makes the same two decisions in the same
   * order.
   *
   * The LOOKUP tree itself is never pruned — an empty category is still a page
   * someone can be standing on, and it has to keep leading to stocked relatives.
   */
  const findChildrenOrSiblings = (tree, handle, options) => {
    const opts = options || {};
    const filtered = (map) => {
      const kept = opts.dropEmpty ? pruneEmpty(map) : map;
      return kept && Object.keys(kept).length ? kept : null;
    };

    const node = findNode(tree, handle);
    if (node && node.subcategories) {
      const children = filtered(node.subcategories);
      if (children) return children;
    }

    if (opts.fallback !== 'siblings') return null;

    const parent = findParent(tree, handle);
    if (!parent || !parent.subcategories) return null;

    const siblings = {};
    for (const name in parent.subcategories) {
      if (!Object.prototype.hasOwnProperty.call(parent.subcategories, name) || isUnsafeKey(name)) continue;
      const sub = parent.subcategories[name];
      // A node with no handle isn't navigable, and the collection being viewed
      // must not appear among its own siblings.
      if (sub && sub.handle && sub.handle !== handle) siblings[name] = sub;
    }
    return filtered(siblings);
  };

  Object.assign(cats, { stripDiacritics, normalize, buildNormMap, cacheKey, readCache, writeCache, load, findAncestry, findNode, findParent, findChildrenOrSiblings, pruneEmpty });
})();
